import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { supabase } from '../../lib/supabase';

const fromLines = () => supabase.from('product_lines');

export interface ProductLine {
  id: string;
  company_id: string;
  name: string;
  description: string | null;
  sort_order: number;
}

export const productLinesKey = (orgId: string | null) => ['product-lines', orgId] as const;

async function fetchLines(orgId: string): Promise<ProductLine[]> {
  const { data, error } = await fromLines()
    .select('id, company_id, name, description, sort_order')
    .eq('organization_id', orgId)
    .is('deleted_at', null)
    .order('sort_order', { ascending: true })
    .order('name', { ascending: true });
  if (error) throw error;
  return data;
}

/** Every line in the org — few enough to load once; filter by company where used. */
export function useProductLines(orgId: string | null) {
  return useQuery({
    queryKey: productLinesKey(orgId),
    queryFn: () => (orgId ? fetchLines(orgId) : Promise.resolve<ProductLine[]>([])),
    enabled: Boolean(orgId),
    staleTime: 60 * 1000,
  });
}

export interface ProductLineInput {
  name: string;
  description: string;
}

export function useUpsertProductLine(orgId: string | null, userId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      companyId,
      values,
      sortOrder,
    }: {
      id: string | null;
      companyId: string;
      values: ProductLineInput;
      sortOrder?: number;
    }) => {
      if (!orgId || !userId) throw new Error('Missing context');
      const description = values.description.trim();
      const fields = { name: values.name.trim(), description: description || null };
      const { error } = id
        ? await fromLines().update(fields).eq('id', id).eq('organization_id', orgId)
        : await fromLines().insert({
            ...fields,
            organization_id: orgId,
            company_id: companyId,
            sort_order: sortOrder ?? 0,
            created_by: userId,
          });
      if (error) {
        if (error.code === '23505') throw new Error('כבר יש ללקוח קו מוצר בשם הזה');
        throw error;
      }
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: productLinesKey(orgId) });
    },
  });
}

export function useDeleteProductLine(orgId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      if (!orgId) throw new Error('Missing context');
      // No deleted_at filter in this table's SELECT policy, so a plain update
      // works. 0026 unlinks the line's projects in the same statement.
      const { error } = await fromLines()
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id)
        .eq('organization_id', orgId);
      if (error) throw error;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: productLinesKey(orgId) });
      await qc.invalidateQueries({ queryKey: ['projects', orgId] });
      await qc.invalidateQueries({ queryKey: ['customer-profile'] });
    },
  });
}
